import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Link as MUILink,
  Checkbox,
  Typography,
  Avatar,
} from '@material-ui/core';
import NotesIcon from '@mui/icons-material/Notes';
import { withStyles } from '@material-ui/core/styles';

import { purple, blackish, gray3, green, gray2 } from 'utils/color-styles';
import { convertDuration } from 'utils/convertDuration';
import { currenciesFormat } from 'utils/currencyFormat';
import CapBalance from 'retired/CapBalance';
import NonprofitLink from 'retired/NonprofitLink';
import getDeedPathSlugFromType from 'utils/getDeedPathSlugFromType';

import { filterOptions } from './DonationsFilter';

const styles = () => ({
  button: {
    padding: '7px 8px',
    minWidth: 64,
    fontSize: '0.8125rem',
    minHeight: 32,
  },
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: blackish,
  },
});

const onClickHandler = (e) => e.stopPropagation();

const StyledLink = withStyles({
  root: {
    '&[disabled]': {
      color: gray2,
      cursor: 'default',
      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
})(MUILink);

const DonationsTable = ({
  header,
  footer,
  donations,
  matchingEnabled,
  matchingMode,
  recurringEnabled,
  isCompany,
  viewIsCorporateDonations,
  classes,
  selectedDonations,
  selectDonation,
  organization,
  donationMatchingRules,
  user,
  loading,
  cancelCorporateDonation,
  viewIsDonors,
  viewIsCompanyDonationOnUserBehalf,
  canViewDonationMatching,
}) => (
  <Table size="small" style={{ marginTop: -10 }}>
    {header}
    <TableBody style={loading ? { opacity: 0.3, cursor: 'wait', pointerEvents: 'none' } : null}>
      {donations
        .map((donation) => {
          const matchedRule =
            donation.matchedRule &&
            donationMatchingRules.find(
              (matchingRule) => matchingRule.get('id') === donation.matchedRule
            );

          const matchingAmountCurrencies = matchingMode
            ? donation.matchableAmountCurrencies
            : donation.matchedAmountCurrencies;
          const differentCurrency = donation.currency !== user.displayCurrency;
          const OriginalCurrencyTooltip = differentCurrency ? Tooltip : Fragment;
          const ProviderTooltip =
            donation.isDeedCredit() || donation.isPayroll() ? Tooltip : Fragment;

          return (
            <TableRow key={donation.id}>
              {matchingEnabled && (
                <TableCell>
                  <div
                    title={
                      matchingMode
                        ? donation.matchedAmount
                          ? 'Matched'
                          : !donation.matchableAmount
                          ? `Unmatchable${
                              donation.matchingError ? `: ${donation.matchingError}` : ''
                            }`
                          : 'Select to match'
                        : donation.matchedAmount
                        ? donation.payoutBatch
                          ? 'Already processed'
                          : 'Select to unmatch'
                        : 'Not matched'
                    }
                  >
                    <Checkbox
                      color="primary"
                      checked={Boolean(
                        matchingMode
                          ? selectedDonations.has(donation.id) || donation.matchedAmount
                          : selectedDonations.has(donation.id)
                      )}
                      onChange={() => selectDonation(donation.id)}
                      indeterminate={!!donation.payoutBatch}
                      disabled={Boolean(
                        matchingMode
                          ? !donation.matchableAmount || donation.matchedAmount
                          : !donation.matchedAmount || donation.payoutBatch
                      )}
                    />
                  </div>
                </TableCell>
              )}

              {isCompany && <TableCell>{donation.currency}</TableCell>}

              <TableCell
                style={{
                  width: 100,
                  textAlign: 'right',
                }}
              >
                <OriginalCurrencyTooltip
                  {...(differentCurrency
                    ? {
                        title: `Amount in original currency: ${currenciesFormat(
                          donation.amountCurrencies,
                          donation.currency
                        )}`,
                      }
                    : {})}
                >
                  <Link
                    to={`/donations/${donation.id}`}
                    style={{
                      fontFamily: 'Andale Mono, Courier, monospace',
                      fontWeight: 'bold',
                      color: donation.processedAt ? green : blackish,
                    }}
                  >
                    {donation.amountCurrencies?.[user.displayCurrency] ? (
                      <>
                        {currenciesFormat(donation.amountCurrencies, user.displayCurrency)}
                        {differentCurrency ? '*' : '⠀'}
                      </>
                    ) : (
                      '-'
                    )}
                  </Link>
                </OriginalCurrencyTooltip>
              </TableCell>

              {(matchingEnabled || canViewDonationMatching) && (
                <TableCell
                  style={{
                    width: 100,
                    textAlign: matchingAmountCurrencies?.[user.displayCurrency]
                      ? 'right'
                      : 'center',
                  }}
                >
                  {matchingAmountCurrencies?.[user.displayCurrency] ? (
                    <OriginalCurrencyTooltip
                      {...(differentCurrency
                        ? {
                            title: `${
                              user.displayCurrency === donation.currency
                                ? `Donation currency: ${donation.currency}`
                                : `Amount in original currency: ${currenciesFormat(
                                    matchingAmountCurrencies,
                                    donation.currency
                                  )}`
                            }${matchedRule && `, ${matchedRule.get('title')}`} on ${moment(
                              donation.matchedAt
                            ).format('MMM DD, YYYY')}`,
                          }
                        : {})}
                    >
                      <Link
                        to={`/donations/${donation.id}`}
                        style={{
                          fontFamily: 'Andale Mono, Courier, monospace',
                          fontWeight: 'bold',
                          color: donation.matchProcessedAt ? green : blackish,
                        }}
                      >
                        {currenciesFormat(matchingAmountCurrencies, user.displayCurrency)}
                        {user.displayCurrency !== donation.currency ? '*' : '⠀'}
                      </Link>
                    </OriginalCurrencyTooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
              )}

              <TableCell style={{ width: 120, textOverflow: 'ellipsis' }}>
                <Link
                  to={`/donations/${donation.id}`}
                  style={{
                    color: blackish,
                  }}
                >
                  <Tooltip title={moment(donation.date).format('HH:mm:ss')}>
                    <span>{moment(donation.date).format('MMM DD, YYYY')}</span>
                  </Tooltip>
                </Link>
              </TableCell>

              {!viewIsCorporateDonations && (
                <TableCell style={{ minWidth: 200 }}>
                  <>
                    <Avatar
                      alt={donation?.user?.name || 'Donor'}
                      src={
                        donation?.user?.mainPicture &&
                        donation.user.mainPicture.replace('http://', 'https://')
                      }
                      style={{
                        width: 32,
                        height: 32,
                        float: 'left',
                        marginRight: 10,
                      }}
                    />
                    {donation.user ? (
                      <Link
                        to={`/volunteers/${donation.user.id}`}
                        className={classes.nameLink}
                        onClick={onClickHandler}
                      >
                        {donation.user.name}
                      </Link>
                    ) : (
                      <Typography
                        component="p"
                        style={{ fontSize: 12 }}
                        className={classes.nameLink}
                      >
                        Anonymous
                      </Typography>
                    )}
                  </>
                </TableCell>
              )}

              {viewIsCorporateDonations && (
                <TableCell style={{ minWidth: 200 }}>
                  {donation.submitter && (
                    <>
                      <Avatar
                        alt={donation.submitter.name}
                        src={
                          donation.submitter.mainPicture &&
                          donation.submitter.mainPicture.replace('http://', 'https://')
                        }
                        style={{
                          width: 32,
                          height: 32,
                          float: 'left',
                          marginRight: 10,
                        }}
                      />
                      <Link
                        to={`/organizers/${donation.submitter.id}`}
                        className={classes.nameLink}
                        onClick={onClickHandler}
                      >
                        {donation.submitter.name}
                      </Link>
                    </>
                  )}
                </TableCell>
              )}

              {matchingMode &&
                (donation.user?._capBalanceByBudget?.length > 0 ? (
                  <TableCell
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Link
                      to={`/donations/budgets/${
                        donation.matchableRule?.budget || donation.user?._capBalanceByBudget[0]?.id
                      }`}
                      style={{
                        fontFamily: 'Andale Mono, Courier, monospace',
                        fontWeight: 'bold',
                        color: blackish,
                      }}
                    >
                      <CapBalance
                        capBalanceByBudget={donation.user?._capBalanceByBudget}
                        budget={
                          donation.matchableRule?.budget ||
                          donation.user?._capBalanceByBudget[0]?.id // Fallback to first budget if no matchable rule exists
                        }
                        unlimitedMessaging="unlimited in"
                      />
                    </Link>
                  </TableCell>
                ) : (
                  <TableCell
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    -
                  </TableCell>
                ))}

              {isCompany ? (
                <TableCell>
                  {donation.nonprofits.map((nonprofit, i) => (
                    <React.Fragment key={nonprofit.id}>
                      <NonprofitLink
                        nonprofit={nonprofit}
                        style={{
                          color: blackish,
                        }}
                      />
                      {i + 1 < donation.nonprofits.size ? ', ' : ''}
                    </React.Fragment>
                  ))}
                  {donation.paymentMethodDetails?.additionalData?.nonprofit?.externalName}
                </TableCell>
              ) : null}

              {viewIsDonors && (
                <TableCell>
                  {donation.deed && donation.deed.id ? (
                    <>
                      {donation.deed.organization?.id === organization.id ||
                      donation.deed.partner?.id === organization.id ? (
                        <Link
                          to={`/deeds/${donation.deed.pathSlug}/${donation.deed.id}`}
                          onClick={onClickHandler}
                          style={{
                            color: blackish,
                          }}
                        >
                          {donation.deedName}
                        </Link>
                      ) : (
                        donation.deedName
                      )}
                    </>
                  ) : donation.isExternal() ? (
                    <Typography component="p" style={{ color: gray3, fontSize: 12 }}>
                      {donation.deedName}
                    </Typography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              )}

              {viewIsCompanyDonationOnUserBehalf && (
                <>
                  <TableCell>
                    {donation.volunteerTime?.date && (
                      <Typography component="p" style={{ fontSize: 12 }}>
                        {moment(donation.volunteerTime?.date).format('MMM DD, YYYY')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {donation.volunteerTime?.duration && (
                      <Typography component="p" style={{ fontSize: 12 }}>
                        {convertDuration(donation.volunteerTime?.duration)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {donation.volunteerTime?.deed?.name && (
                      <Link
                        to={`/deeds/${getDeedPathSlugFromType(donation.volunteerTime?.deed?.__t)}/${
                          donation.volunteerTime?.deed?._id
                        }`}
                        onClick={onClickHandler}
                        style={{
                          color: blackish,
                        }}
                      >
                        {donation.volunteerTime?.deed?.name}
                      </Link>
                    )}
                  </TableCell>
                </>
              )}

              <TableCell>
                {donation.isExternal() ? (
                  <Typography component="p" style={{ color: gray3, fontSize: 12 }}>
                    {donation.providerName}
                  </Typography>
                ) : (
                  <ProviderTooltip
                    {...(donation.isDeedCredit() || donation.isPayroll()
                      ? { title: donation.isDeedCredit() ? "From user's Deed Balance" : 'Payroll' }
                      : {})}
                  >
                    <span>
                      {donation.providerName}
                      {donation.isDeedCredit() || donation.isPayroll() ? '*' : ''}
                    </span>
                  </ProviderTooltip>
                )}
              </TableCell>

              {matchingEnabled && !matchingMode ? (
                <TableCell>{donation.matchProcessedAt ? donation.matchingProvider : '-'}</TableCell>
              ) : null}

              {recurringEnabled && (
                <TableCell>
                  {filterOptions.find(
                    ({ id }) => id === (donation.donationSchedule?.frequency || '0')
                  )?.name || '-'}
                </TableCell>
              )}
              {viewIsCorporateDonations && (
                <TableCell>
                  {donation.note ? (
                    <Tooltip title={donation.note} arrow>
                      <NotesIcon style={{ color: purple }} />
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
              )}
              {viewIsCorporateDonations && (
                <TableCell>
                  {donation.paymentMethodDetails?.type === 'CorporateBudget' ? (
                    donation.status === 'Success' ? (
                      donation.payoutBatch === null ? (
                        <Tooltip title="This donation is pending processing" arrow>
                          <StyledLink
                            disabled={loading}
                            onClick={() => {
                              cancelCorporateDonation(donation);
                            }}
                          >
                            Cancel donation
                          </StyledLink>
                        </Tooltip>
                      ) : (
                        <Tooltip title="This donation has already been processed" arrow>
                          <span>{' – '}</span>
                        </Tooltip>
                      )
                    ) : (
                      <Tooltip title="This donation has already been cancelled" arrow>
                        <StyledLink disabled>Cancelled</StyledLink>
                      </Tooltip>
                    )
                  ) : (
                    ' – '
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })
        .toArray()}
    </TableBody>
    {footer}
  </Table>
);

DonationsTable.propTypes = {
  donations: PropTypes.object,
  matchingMode: PropTypes.bool,
  canViewDonationMatching: PropTypes.bool,
  matchingEnabled: PropTypes.bool,
  recurringEnabled: PropTypes.bool,
  isCompany: PropTypes.bool,
  viewIsCorporateDonations: PropTypes.bool,
  organization: PropTypes.object,
  donationMatchingRules: PropTypes.object,
  selectedDonations: PropTypes.object,
  selectDonation: PropTypes.func,
  header: PropTypes.object,
  footer: PropTypes.object,
  classes: PropTypes.object,
  user: PropTypes.object,
  loading: PropTypes.bool,
  cancelCorporateDonation: PropTypes.func,
  viewIsDonors: PropTypes.bool,
  viewIsCompanyDonationOnUserBehalf: PropTypes.bool,
};

export default withStyles(styles)(DonationsTable);
